import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import {
  ExecuteSelectLoadingPage,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/ExecuteSelectLoadingPage';
import {
  FDContentContainer, FDLozenge, FDStack, FDTitleText, FDTooltip,
} from '@facet-wealth/design';
import {
  FadeInBox,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedCommon/ExecuteSelectLoadingPage/FadeInBox';
import React from 'react';
import useUserContext from 'Context/UserContext';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import fr from 'facet-rest';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { metaOverrideSet as ScenarioSelectionMetaOverrideSet } from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/ScenarioSelectionRedefinedStepLayoutWithContentHeader/metaOverrideSet';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import { RoadmapExecutiveSummaryOverview } from './RoadmapExecutiveSummaryOverview/RoadmapExecutiveSummaryOverview';
import RoadmapExecutiveSummaryOverviewMetaOverrideSet from './RoadmapExecutiveSummaryOverview/metaOverrideSet';
import { RoadmapExecutiveSummaryNextUp } from './RoadmapExecutiveSummaryNextUp/RoadmapExecutiveSummaryNextUp';
import RoadmapExecutiveSummaryNextUpMetaOverrideSet from './RoadmapExecutiveSummaryNextUp/metaOverrideSet';
import { RoadmapExecutiveSummaryActions } from './RoadmapExecutiveSummaryActions/RoadmapExecutiveSummaryActions';
import RoadmapExecutiveSummaryActionsMetaOverrideSet from './RoadmapExecutiveSummaryActions/metaOverrideSet';
import { RoadmapExecutiveSummaryQuestions } from './RoadmapExecutiveSummaryQuestions/RoadmapExecutiveSummaryQuestions';
import RoadmapExecutiveSummaryQuestionsMetaOverrideSet from './RoadmapExecutiveSummaryQuestions/metaOverrideSet';

const ROADMAP_EXECUTIVE_SUMMARY_GOAL_OVERVIEW_SECTION_ID = 'roadmap-executive-summary-goal-overview-section';
const ROADMAP_EXECUTIVE_SUMMARY_NEXT_UP_SECTION_ID = 'roadmap-executive-summary-next-up-section';
const ROADMAP_EXECUTIVE_SUMMARY_ACTIONS_SECTION_ID = 'roadmap-executive-summary-actions-section';
const ROADMAP_EXECUTIVE_SUMMARY_QUESTION_SECTION_ID = 'roadmap-executive-summary-questions-section';

// Keep in mind when editing this component that this is used both on the intro page (both for the agent and client)
// and in the decision step within the scenario selection wizard.
export const RoadmapExecutiveSummaryContent = ({ resource, showHeaderAndFooter = true }) => {
  const { isAgent } = useUserContext();
  useBoneBackgroundColor();

  const { isSelecting } = useSelectScenarioContext();
  const isMobile = useMobileLayout();

  const overviewSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_GOAL_OVERVIEW_SECTION_ID);
  const nextUpSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_NEXT_UP_SECTION_ID);
  const actionsSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_ACTIONS_SECTION_ID);
  const questionsSection = resource.attr('sections')?.resources().findItem((section) => section.id() === ROADMAP_EXECUTIVE_SUMMARY_QUESTION_SECTION_ID);

  const proposalExpirationRel = resource.withRelationship(
    fr.relationships.proposalExpiration.name,
    (rel) => rel,
    () => null,
  );
  const shouldAutoOpenCFPMessage = !resource.isTruthy('isProposalViewed') && !isAgent && !proposalExpirationRel;

  if (resource.isTruthy('shouldExecuteSelect') && isSelecting) {
    return <ExecuteSelectLoadingPage resource={resource} />;
  }

  const header = showHeaderAndFooter ? (
    <FDStack direction={'row'} spacing={2} alignItems={'center'} wrap>
      <FDTitleText value={resource.attr('scenarioName')} size={'lg'} {...dataAttrs('current-proposal-scenario-name')} />
      <FDStack alignItems={'center'}>
        <FDTooltip placement={'bottom'} title={'Roadmaps expire to ensure your actions remain fresh.'}>
          <FDLozenge
            color={resource.attr('status')}
            size={'lg'}
            label={resource.attr('statusLabel')}
            {...dataAttrs('current-proposal-scenario-status-lozenge')}
          />
        </FDTooltip>
      </FDStack>
    </FDStack>
  ) : null;

  const OverviewSectionComponent = overviewSection ? (
    <MetaResourceRenderer
      resource={overviewSection}
      layout={RoadmapExecutiveSummaryOverview}
      metaOverrideSet={RoadmapExecutiveSummaryOverviewMetaOverrideSet}
      LayoutProps={{
        shouldAutoOpenCFPMessage,
      }}
    />
  ) : null;

  const ActionsSectionComponent = actionsSection ? (
    <MetaResourceRenderer
      resource={actionsSection}
      layout={RoadmapExecutiveSummaryActions}
      metaOverrideSet={RoadmapExecutiveSummaryActionsMetaOverrideSet}
    />
  ) : null;

  const NextUpSectionComponent = nextUpSection ? (
    <MetaResourceRenderer
      resource={nextUpSection}
      layout={RoadmapExecutiveSummaryNextUp}
      metaOverrideSet={RoadmapExecutiveSummaryNextUpMetaOverrideSet}
      LayoutProps={{
        ActionsSectionComponent,
      }}
    />
  ) : null;

  const QuestionsSectionComponent = questionsSection ? (
    <MetaResourceRenderer
      resource={questionsSection}
      layout={RoadmapExecutiveSummaryQuestions}
      metaOverrideSet={RoadmapExecutiveSummaryQuestionsMetaOverrideSet}
    />
  ) : null;

  const footer = showHeaderAndFooter ? (
    <MetaControl attrName={'disclaimerText'} resource={resource} metaOverrideSet={ScenarioSelectionMetaOverrideSet} />
  ) : null;

  let paddingY = isMobile ? 4 : 2;
  if (showHeaderAndFooter && !isMobile) {
    paddingY = 8;
  }

  return (
    <FDContentContainer maxWidth="xl" minHeight={'100dvh'} flexGrow={1}>
      <FadeInBox>
        <FDStack direction={'column'} paddingY={paddingY} spacing={3}>
          {header}
          <FDStack direction={'column'} spacing={12}>
            <FDStack direction={'row'} spacing={8} wrap={isMobile}>
              {OverviewSectionComponent}
              <FDStack direction={'column'} paddingY={paddingY} spacing={3} maxWidth="100%">
                {NextUpSectionComponent}
                {QuestionsSectionComponent}
              </FDStack>
            </FDStack>
            {footer}
          </FDStack>
        </FDStack>
      </FadeInBox>
    </FDContentContainer>
  );
};
